// ProjectPlot.tsx - With KDE Integration and Demographic KDEs
import { Layout } from 'plotly.js';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Plot from 'react-plotly.js';
import {
  CongressData,
  DtType,
  EarlyStageVcData,
  FortuneCeoData,
  ModelData,
  MoviesMediumFullData,
  MoviesSimpleData,
  PerspectiveBaseInfoFragment,
  ProjectType,
  useProjectQuery,
} from '../../generated/graphql';
import { HelivanColor } from '../helivan/HelivanColor';
import {
  Box,
  Flex,
  HStack,
  Icon,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tooltip,
} from '@chakra-ui/react';
import { HelivanText } from '../helivan/HelivanText';
import { formatVersionLabel } from '../../utils/formatDate';
import { useIsMobile } from '../../utils/isMobile';
import { IoChevronForward } from 'react-icons/io5';
import { RiInformationLine } from 'react-icons/ri';

// Shared tooltip text explaining the control band / change detection.
const CONTROL_BAND_HELP =
  'The expected range based on past check-ins (mean ± a few standard deviations). A point that lands outside it is flagged as a change.';

interface ProjectPlotProps {
  type: 'topic' | 'subtopic';
  topic: string;
  perspectives: PerspectiveBaseInfoFragment[];
  setDigitalTwinId: (id: string | undefined) => void;
  leftTitle?: string;
  rightTitle?: string;
  selectedTwins: Set<{ id: string; name: string }>;
  setSelectedTwins: React.Dispatch<
    React.SetStateAction<Set<{ id: string; name: string }>>
  >;
  selectedDemographics?: Set<{ key: string; value: string }>;
  showMovement?: boolean;
  comparisonPerspectives?: PerspectiveBaseInfoFragment[];
  timelineProjects?: Array<{ id: string; dateCreated: string }>;
  currentProjectId?: string;
  comparisonProjectId?: string;
  allTimelinePerspectives?: Map<string, PerspectiveBaseInfoFragment[]>;
  onTabChange?: (tabIndex: number) => void;
  projectType: ProjectType;
  requestTimelineData?: () => void;
  initialTabIndex?: number;
  // Change detection settings
  changeDetectionEnabled?: boolean;
  sensitivityStdDevs?: number;
  burnInPeriod?: number;
}

// Helper to get entity info from perspective (either digitalTwin or customAgent)
function getEntityInfo(perspective: PerspectiveBaseInfoFragment): {
  id: string;
  name: string;
  isCustomAgent: boolean;
  digitalTwin: PerspectiveBaseInfoFragment['digitalTwin'];
  customAgent: PerspectiveBaseInfoFragment['customAgent'];
} {
  if (perspective.digitalTwin) {
    return {
      id: perspective.digitalTwin.id,
      name: perspective.digitalTwin.name,
      isCustomAgent: false,
      digitalTwin: perspective.digitalTwin,
      customAgent: null,
    };
  }
  if (perspective.customAgent) {
    return {
      id: perspective.customAgent.id,
      name: perspective.customAgent.name,
      isCustomAgent: true,
      digitalTwin: null,
      customAgent: perspective.customAgent,
    };
  }
  // Fallback (should never happen)
  return {
    id: 'unknown',
    name: 'Unknown',
    isCustomAgent: false,
    digitalTwin: null,
    customAgent: null,
  };
}

// KDE Gaussian kernel function
function gaussianKernel(x: number, mean: number, bandwidth: number): number {
  return (
    Math.exp(-0.5 * Math.pow((x - mean) / bandwidth, 2)) /
    (bandwidth * Math.sqrt(2 * Math.PI))
  );
}

// KDE calculation function
function calculateKDE(
  data: number[],
  bandwidth: number,
  bins: number = 100
): { x: number[]; y: number[] } {
  // Find the range of the data
  const min = Math.min(...data);
  const max = Math.max(...data);

  // Add padding to the range
  const padding = (max - min) * 0.1;
  const paddedMin = min - padding;
  const paddedMax = max + padding;

  // Create evenly spaced x values for the KDE
  const x: number[] = [];
  const step = (paddedMax - paddedMin) / (bins - 1);
  for (let i = 0; i < bins; i++) {
    x.push(paddedMin + i * step);
  }

  // Calculate density values for each x
  const y: number[] = x.map((point) => {
    // Sum the kernel values for each data point
    return (
      data.reduce((sum, dataPoint) => {
        return sum + gaussianKernel(point, dataPoint, bandwidth);
      }, 0) / data.length
    );
  });

  return { x, y };
}

// Helper function to calculate Silverman's rule bandwidth
function calculateBandwidth(data: number[]): number {
  const standardDeviation = Math.sqrt(
    data.reduce(
      (sum, x) =>
        sum + Math.pow(x - data.reduce((a, b) => a + b, 0) / data.length, 2),
      0
    ) / data.length
  );
  return 0.9 * standardDeviation * Math.pow(data.length, -0.2);
}

// Helper to add alpha to a hex color string (e.g. '#FF6B35' → 'rgba(255,107,53,0.15)')
function colorWithAlpha(color: string, alpha: number): string {
  // Handle hex colors
  if (color.startsWith('#')) {
    const hex = color.slice(1);
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return `rgba(${r},${g},${b},${alpha})`;
  }
  // Handle rgba — replace existing alpha
  if (color.startsWith('rgba')) {
    return color.replace(/,[^,]+\)$/, `,${alpha})`);
  }
  // Handle rgb — convert to rgba
  if (color.startsWith('rgb')) {
    return color.replace('rgb', 'rgba').replace(')', `,${alpha})`);
  }
  return color;
}

// Helper function to calculate moving statistics for error regions
interface MovingStats {
  mean: number;
  stdDev: number;
  upperBound: number;
  lowerBound: number;
}

function calculateMovingStats(
  values: number[],
  windowSize: number,
  sensitivity: number
): MovingStats[] {
  const stats: MovingStats[] = [];

  for (let i = 0; i < values.length; i++) {
    // Use all available points up to this point, capped by window size
    const startIdx = Math.max(0, i - windowSize + 1);
    const window = values.slice(startIdx, i + 1);

    // Calculate mean
    const mean = window.reduce((sum, v) => sum + v, 0) / window.length;

    // Calculate standard deviation
    const variance = window.reduce((sum, v) => sum + Math.pow(v - mean, 2), 0) / window.length;
    const stdDev = Math.sqrt(variance);

    stats.push({
      mean,
      stdDev,
      upperBound: mean + sensitivity * stdDev,
      lowerBound: mean - sensitivity * stdDev,
    });
  }

  return stats;
}

// Helper function to project future error bounds based on recent trend
function projectFutureBounds(
  values: number[],
  dates: number[],
  numPoints: number,
  windowSize: number,
  sensitivity: number
): { dates: number[]; upper: number[]; lower: number[]; projected: number[] } {
  if (values.length < 2) {
    return { dates: [], upper: [], lower: [], projected: [] };
  }

  // Calculate the recent trend (linear regression on last windowSize points)
  const recentStart = Math.max(0, values.length - windowSize);
  const recentValues = values.slice(recentStart);
  const recentDates = dates.slice(recentStart);

  // Simple linear regression
  const n = recentValues.length;
  const sumX = recentDates.reduce((a, b) => a + b, 0);
  const sumY = recentValues.reduce((a, b) => a + b, 0);
  const sumXY = recentDates.reduce((sum, x, i) => sum + x * recentValues[i], 0);
  const sumXX = recentDates.reduce((sum, x) => sum + x * x, 0);

  const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX) || 0;
  const intercept = (sumY - slope * sumX) / n;

  // Calculate standard deviation of recent values
  const mean = sumY / n;
  const variance = recentValues.reduce((sum, v) => sum + Math.pow(v - mean, 2), 0) / n;
  const stdDev = Math.sqrt(variance);

  // Project future points
  const lastDate = dates[dates.length - 1];
  const dateStep = dates.length > 1 ? dates[dates.length - 1] - dates[dates.length - 2] : 1;

  const futureDates: number[] = [];
  const projected: number[] = [];
  const upper: number[] = [];
  const lower: number[] = [];

  for (let i = 1; i <= numPoints; i++) {
    const futureDate = lastDate + i * dateStep;
    const projectedValue = slope * futureDate + intercept;
    // Widen the error bounds for future projections (uncertainty increases)
    const uncertaintyFactor = 1 + i * 0.1;

    futureDates.push(futureDate);
    projected.push(projectedValue);
    upper.push(projectedValue + sensitivity * stdDev * uncertaintyFactor);
    lower.push(projectedValue - sensitivity * stdDev * uncertaintyFactor);
  }

  return { dates: futureDates, upper, lower, projected };
}

// Helper function to calculate aggregate flow vectors
// This creates a grid and calculates average movement in each region
interface FlowVector {
  x: number;
  y: number;
  dx: number;
  dy: number;
  magnitude: number;
}

function calculateFlowVectors(
  currentPerspectives: PerspectiveBaseInfoFragment[],
  comparisonPerspectives: PerspectiveBaseInfoFragment[],
  gridSize: number = 5
): FlowVector[] {
  // Create a map of entity ID to perspectives for quick lookup
  const comparisonMap = new Map<string, PerspectiveBaseInfoFragment>();
  comparisonPerspectives.forEach((p) => {
    const entity = getEntityInfo(p);
    comparisonMap.set(entity.id, p);
  });

  // Calculate movements for each entity that exists in both datasets
  const movements: Array<{
    oldX: number;
    oldY: number;
    newX: number;
    newY: number;
    dx: number;
    dy: number;
  }> = [];

  currentPerspectives.forEach((current) => {
    const entity = getEntityInfo(current);
    const comparison = comparisonMap.get(entity.id);
    if (comparison) {
      movements.push({
        oldX: comparison.x,
        oldY: comparison.y,
        newX: current.x,
        newY: current.y,
        dx: current.x - comparison.x,
        dy: current.y - comparison.y,
      });
    }
  });

  if (movements.length === 0) {
    return [];
  }

  // Find bounds for the grid
  const allXValues = [
    ...currentPerspectives.map((p) => p.x),
    ...comparisonPerspectives.map((p) => p.x),
  ];
  const allYValues = [
    ...currentPerspectives.map((p) => p.y),
    ...comparisonPerspectives.map((p) => p.y),
  ];

  const minX = Math.min(...allXValues);
  const maxX = Math.max(...allXValues);
  const minY = Math.min(...allYValues);
  const maxY = Math.max(...allYValues);

  const cellWidth = (maxX - minX) / gridSize;
  const cellHeight = (maxY - minY) / gridSize;

  // Create grid and aggregate movements
  const flowVectors: FlowVector[] = [];

  for (let i = 0; i < gridSize; i++) {
    for (let j = 0; j < gridSize; j++) {
      const cellX = minX + (i + 0.5) * cellWidth;
      const cellY = minY + (j + 0.5) * cellHeight;

      // Find movements that originated in this cell
      const cellMovements = movements.filter((m) => {
        return (
          m.oldX >= minX + i * cellWidth &&
          m.oldX < minX + (i + 1) * cellWidth &&
          m.oldY >= minY + j * cellHeight &&
          m.oldY < minY + (j + 1) * cellHeight
        );
      });

      if (cellMovements.length > 0) {
        // Calculate average movement
        const avgDx =
          cellMovements.reduce((sum, m) => sum + m.dx, 0) /
          cellMovements.length;
        const avgDy =
          cellMovements.reduce((sum, m) => sum + m.dy, 0) /
          cellMovements.length;
        const magnitude = Math.sqrt(avgDx * avgDx + avgDy * avgDy);

        // Only include if there's significant movement
        if (magnitude > 0.05) {
          flowVectors.push({
            x: cellX,
            y: cellY,
            dx: avgDx,
            dy: avgDy,
            magnitude,
          });
        }
      }
    }
  }

  return flowVectors;
}

export const ProjectPlot = React.memo(
  ({
    type,
    perspectives,
    topic,
    setDigitalTwinId,
    selectedTwins,
    leftTitle,
    rightTitle,
    setSelectedTwins,
    selectedDemographics,
    showMovement,
    comparisonPerspectives,
    timelineProjects,
    currentProjectId,
    comparisonProjectId,
    allTimelinePerspectives,
    onTabChange,
    projectType,
    requestTimelineData,
    initialTabIndex = 0,
    changeDetectionEnabled = true,
    sensitivityStdDevs = 2.0,
    burnInPeriod = 10,
  }: ProjectPlotProps) => {
    // Phones: shrink the Plotly margins/fonts so the fluid chart isn't crushed.
    const isMobile = useIsMobile();
    // Tab state: 0 = Timeline, 1 = All Perspectives
    const [tabIndex, setTabIndex] = useState(initialTabIndex);

    // State for collapsible Significant Shifts drawer
    const [significantShiftsExpanded, setSignificantShiftsExpanded] =
      useState(false);

    // Notify parent of tab changes
    const handleTabChange = (index: number) => {
      setTabIndex(index);
      onTabChange?.(index);
      // Request timeline data when switching to Timeline tab
      if (index === 0) {
        requestTimelineData?.();
      }
    };

    // Request timeline data on mount if starting on Timeline tab
    useEffect(() => {
      if (initialTabIndex === 0) {
        requestTimelineData?.();
      }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Check if we have a timeline and selected digital twins or demographic filter
    // Use >= 1 so single-project timelines work (the current project counts as 1)
    const hasTimeline = timelineProjects && timelineProjects.length >= 1;
    const hasSelectedTwins = selectedTwins.size > 0;
    const hasDemographicFilter = selectedDemographics && selectedDemographics.size > 0;
    const canShowTimeline = hasTimeline && (hasSelectedTwins || hasDemographicFilter);

    // Get selected digital twin IDs (support multiple)
    const selectedTwinIds = useMemo(() =>
      Array.from(selectedTwins).map(t => t.id),
      [selectedTwins]
    );

    // Get personas that match the demographic filter
    const demographicMatchedTwins = useMemo(() => {
      if (!hasDemographicFilter) return [];
      return perspectives.filter(p => {
        const dt = p.digitalTwin;
        if (!dt) return false; // Custom agents don't have demographics
        const congressData = dt.congressData;
        const moviesMediumFullData = dt.moviesMediumFullData;
        const moviesSimpleData = dt.moviesSimpleData;
        const earlyStageVcData = dt.earlyStageVcData;
        const fortuneCeoData = dt.fortuneCeoData;
        const modelData = dt.modelData;
        return hasDemographics(
          selectedDemographics,
          congressData ?? undefined,
          moviesMediumFullData ?? undefined,
          moviesSimpleData ?? undefined,
          earlyStageVcData ?? undefined,
          fortuneCeoData ?? undefined,
          modelData ?? undefined
        );
      }).map(p => {
        const entity = getEntityInfo(p);
        return { id: entity.id, name: entity.name };
      });
    }, [perspectives, selectedDemographics, hasDemographicFilter]);

    // Fetch all timeline projects to get selected twins' positions across time
    // Now supports multiple personas with their assigned colors
    const [timelineData, setTimelineData] = useState<
      Map<string, Array<{
        projectId: string;
        dateCreated: string;
        x: number;
        y: number;
        name: string;
        color: string;
      }>>
    >(new Map());

    // State for loading timeline data
    const [loadingTimeline, setLoadingTimeline] = useState(false);

    // Fetch timeline data when in Timeline tab and twins are selected (or demographic filter is active)
    useEffect(() => {
      // Determine which twins to show timelines for
      const twinsToShow = selectedTwins.size > 0
        ? Array.from(selectedTwins)
        : demographicMatchedTwins;

      if (
        tabIndex === 0 &&
        twinsToShow.length > 0 &&
        timelineProjects &&
        timelineProjects.length > 0 &&
        allTimelinePerspectives &&
        allTimelinePerspectives.size > 0
      ) {
        setLoadingTimeline(true);

        const newTimelineData = new Map<string, Array<{
          projectId: string;
          dateCreated: string;
          x: number;
          y: number;
          name: string;
          color: string;
        }>>();

        const topicToMatch = type === 'topic' ? 'topic' : topic;
        const isDemographicMode = selectedTwins.size === 0 && demographicMatchedTwins.length > 0;

        // Get positions for each twin
        twinsToShow.forEach((twin, twinIndex) => {
          const positions: Array<{
            projectId: string;
            dateCreated: string;
            x: number;
            y: number;
            name: string;
            color: string;
          }> = [];

          // For demographic mode, all use the same color (green)
          // For individual selection, each gets a different color
          const color = isDemographicMode
            ? '#52d152'
            : PERSONA_COLORS[twinIndex % PERSONA_COLORS.length];

          timelineProjects.forEach((timelineProject) => {
            const projectPerspectives = allTimelinePerspectives.get(timelineProject.id);

            if (projectPerspectives) {
              const perspective = projectPerspectives.find(
                (p) => getEntityInfo(p).id === twin.id && p.topic === topicToMatch
              );

              if (perspective) {
                positions.push({
                  projectId: timelineProject.id,
                  dateCreated: timelineProject.dateCreated,
                  x: perspective.x,
                  y: perspective.y,
                  name: twin.name,
                  color,
                });
              }
            }
          });

          // Sort by date
          positions.sort(
            (a, b) => new Date(a.dateCreated).getTime() - new Date(b.dateCreated).getTime()
          );

          if (positions.length > 0) {
            newTimelineData.set(twin.id, positions);
          }
        });

        setTimelineData(newTimelineData);
        setLoadingTimeline(false);
      } else {
        setTimelineData(new Map());
        setLoadingTimeline(false);
      }
    }, [
      tabIndex,
      selectedTwinIds,
      selectedTwins,
      demographicMatchedTwins,
      timelineProjects,
      topic,
      type,
      allTimelinePerspectives,
    ]);

    // Refs to avoid unnecessary re-renders
    const plotRef = useRef(null);
    const perspectivesRef = useRef(perspectives);

    // Update ref when perspectives change
    useEffect(() => {
      perspectivesRef.current = perspectives;
    }, [perspectives]);

    // Pre-compute selected twin names for faster lookups
    const selectedTwinNamesMap = useMemo(() => {
      const map = new Map<string, boolean>();
      Array.from(selectedTwins).forEach((twin) => {
        map.set(twin.name, true);
      });
      return map;
    }, [selectedTwins]);

    // Color palette for multi-select personas
    const PERSONA_COLORS = useMemo(() => [
      '#c95149', // red
      '#4CAF50', // green
      '#2196F3', // blue
      '#FF9800', // orange
      '#9C27B0', // purple
      '#00BCD4', // cyan
      '#E91E63', // pink
      '#795548', // brown
    ], []);

    // Map selected twins to colors
    const selectedTwinColorMap = useMemo(() => {
      const colorMap = new Map<string, string>();
      Array.from(selectedTwins).forEach((twin, index) => {
        colorMap.set(twin.id, PERSONA_COLORS[index % PERSONA_COLORS.length]);
      });
      return colorMap;
    }, [selectedTwins, PERSONA_COLORS]);

    const filteringOn = selectedTwins.size !== 0;

    const hasDemographics = (
      selectedDemographics: Set<{ key: string; value: string }> | undefined,
      congress?: CongressData,
      moviesFullMedium?: MoviesMediumFullData,
      moviesSimple?: MoviesSimpleData,
      earlyStage?: EarlyStageVcData,
      fortuneCeo?: FortuneCeoData,
      modelData?: ModelData
    ): boolean => {
      if (!selectedDemographics || selectedDemographics.size === 0) {
        return false;
      }

      // Check if ALL selected demographics match
      return Array.from(selectedDemographics).every((demographic) => {
        const { key, value } = demographic;

        if (congress && Object(congress).hasOwnProperty(key)) {
          // @ts-ignore
          return congress[key] === value;
        }
        if (moviesFullMedium && Object(moviesFullMedium).hasOwnProperty(key)) {
          // @ts-ignore
          return moviesFullMedium[key] === value;
        }
        if (moviesSimple && Object(moviesSimple).hasOwnProperty(key)) {
          // @ts-ignore
          return moviesSimple[key] === value;
        }
        if (earlyStage && Object(earlyStage).hasOwnProperty(key)) {
          // @ts-ignore
          return earlyStage[key] === value;
        }
        if (fortuneCeo && Object(fortuneCeo).hasOwnProperty(key)) {
          // @ts-ignore
          return fortuneCeo[key] === value;
        }
        if (modelData && Object(modelData).hasOwnProperty(key)) {
          // @ts-ignore
          return modelData[key] === value;
        }

        return false;
      });
    };

    // Group perspectives by demographic for KDE
    const groupedPerspectives = useMemo(() => {
      if (
        !selectedDemographics ||
        selectedDemographics.size === 0 ||
        type !== 'subtopic'
      ) {
        return {
          all: perspectives,
          withDemographic: [],
          withoutDemographic: [],
        };
      }

      const withDemographic: PerspectiveBaseInfoFragment[] = [];
      const withoutDemographic: PerspectiveBaseInfoFragment[] = [];

      perspectives.forEach((perspective) => {
        const dt = perspective.digitalTwin;
        const congressData = dt?.congressData;
        const moviesMediumFullData = dt?.moviesMediumFullData;
        const moviesSimpleData = dt?.moviesSimpleData;
        const earlyStageData = dt?.earlyStageVcData;
        const fortuneCeoData = dt?.fortuneCeoData;
        const modelData = dt?.modelData;

        if (
          dt && hasDemographics(
            selectedDemographics,
            congressData ?? undefined,
            moviesMediumFullData ?? undefined,
            moviesSimpleData ?? undefined,
            earlyStageData ?? undefined,
            fortuneCeoData ?? undefined,
            modelData ?? undefined
          )
        ) {
          withDemographic.push(perspective);
        } else {
          withoutDemographic.push(perspective);
        }
      });

      return {
        all: perspectives,
        withDemographic,
        withoutDemographic,
      };
    }, [perspectives, selectedDemographics, type]);

    // Calculate KDE data for all groups
    const kdeData = useMemo(() => {
      if (type !== 'subtopic' || perspectives.length < 2) {
        return null;
      }

      const result: {
        all: { x: number[]; y: number[] } | null;
        withDemographic: { x: number[]; y: number[] } | null;
        withoutDemographic: { x: number[]; y: number[] } | null;
      } = {
        all: null,
        withDemographic: null,
        withoutDemographic: null,
      };

      // Calculate KDE for all perspectives
      const allXValues = groupedPerspectives.all.map((p) => p.x);
      if (allXValues.length >= 2) {
        const bandwidth = calculateBandwidth(allXValues);
        result.all = calculateKDE(allXValues, bandwidth);
      }

      // Only calculate demographic KDEs if demographic filter is active and we have enough data points
      if (selectedDemographics && selectedDemographics.size > 0) {
        // Calculate KDE for perspectives with the demographic
        const withDemographicXValues = groupedPerspectives.withDemographic.map(
          (p) => p.x
        );
        // Only calculate KDE if we have at least 10 twins in the demographic
        if (withDemographicXValues.length >= 10) {
          const bandwidth = calculateBandwidth(withDemographicXValues);
          result.withDemographic = calculateKDE(
            withDemographicXValues,
            bandwidth
          );
        }

        // Calculate KDE for perspectives without the demographic
        const withoutDemographicXValues =
          groupedPerspectives.withoutDemographic.map((p) => p.x);
        // Only calculate KDE if we have at least 10 twins without the demographic
        if (withoutDemographicXValues.length >= 10) {
          const bandwidth = calculateBandwidth(withoutDemographicXValues);
          result.withoutDemographic = calculateKDE(
            withoutDemographicXValues,
            bandwidth
          );
        }
      }

      return result;
    }, [groupedPerspectives, type, perspectives.length, selectedDemographics]);

    // Helper function to get demographics text for display
    const getDemographicsText = (): string => {
      if (!selectedDemographics || selectedDemographics.size === 0) {
        return 'All';
      }

      // Join all demographic keys and values
      return Array.from(selectedDemographics)
        .map((demo) => `${demo.value}`)
        .join(', ');
    };

    // Calculate shift summary separately so it can be used in layout annotations
    const shiftSummary = useMemo(() => {
      if (
        !showMovement ||
        !comparisonPerspectives ||
        comparisonPerspectives.length === 0
      ) {
        return null;
      }

      const comparisonMap = new Map<string, PerspectiveBaseInfoFragment>();
      comparisonPerspectives.forEach((p) => {
        const entity = getEntityInfo(p);
        comparisonMap.set(entity.id, p);
      });

      let rightShift = 0;
      let leftShift = 0;
      let upShift = 0;
      let downShift = 0;
      let noChange = 0;
      let matchedCount = 0;

      const magnitudes: number[] = [];
      const individualShifts: Array<{
        name: string;
        dx: number;
        dy: number;
        magnitude: number;
        direction: string;
      }> = [];

      perspectives.forEach((current) => {
        const entity = getEntityInfo(current);
        const comparison = comparisonMap.get(entity.id);
        if (comparison) {
          matchedCount++;
          const dx = current.x - comparison.x;
          const dy = current.y - comparison.y;
          const magnitude = Math.sqrt(dx * dx + dy * dy);
          magnitudes.push(magnitude);

          // Determine primary direction for display
          let direction = '';
          if (Math.abs(dx) > Math.abs(dy)) {
            direction = dx > 0 ? 'right' : 'left';
          } else {
            direction = dy > 0 ? 'up' : 'down';
          }

          // Track individual shifts for significant movers
          individualShifts.push({
            name: entity.name,
            dx,
            dy,
            magnitude,
            direction,
          });

          // Adjusted threshold - consider movement > 0.05 as significant
          if (magnitude < 0.05) {
            noChange++;
          } else {
            // Categorize by primary direction
            if (Math.abs(dx) > Math.abs(dy)) {
              if (dx > 0) rightShift++;
              else leftShift++;
            } else {
              if (dy > 0) upShift++;
              else downShift++;
            }
          }
        }
      });

      const totalMovers = rightShift + leftShift + upShift + downShift;
      const total = totalMovers + noChange;

      // Calculate statistics for debugging
      const medianMagnitude =
        magnitudes.length > 0
          ? (() => {
              const sorted = [...magnitudes].sort((a, b) => a - b);
              const mid = Math.floor(sorted.length / 2);
              return sorted.length % 2 === 0
                ? (sorted[mid - 1] + sorted[mid]) / 2
                : sorted[mid];
            })()
          : 0;
      const maxMagnitude = magnitudes.length > 0 ? Math.max(...magnitudes) : 0;

      // Find significant movers (>= 0.5 magnitude)
      const significantMovers = individualShifts
        .filter((shift) => shift.magnitude >= 0.5)
        .sort((a, b) => b.magnitude - a.magnitude) // Sort by magnitude descending
        .slice(0, 10); // Take top 10

      return {
        rightShift,
        leftShift,
        upShift,
        downShift,
        noChange,
        totalMovers,
        total,
        matchedCount,
        rightPct: total > 0 ? (rightShift / total) * 100 : 0,
        leftPct: total > 0 ? (leftShift / total) * 100 : 0,
        upPct: total > 0 ? (upShift / total) * 100 : 0,
        downPct: total > 0 ? (downShift / total) * 100 : 0,
        noChangePct: total > 0 ? (noChange / total) * 100 : 0,
        medianMagnitude,
        maxMagnitude,
        significantMovers,
      };
    }, [showMovement, comparisonPerspectives, perspectives]);

    // Create data array with memoization to prevent unnecessary recalculation
    const data = useMemo(() => {
      // For Evaluation projects on the overall graph, use a single trace with color gradient
      if (projectType === ProjectType.Evaluation && type === 'topic') {
        const xValues: number[] = [];
        const yValues: number[] = [];
        const scores: number[] = [];
        const textLabels: string[] = [];
        const customDataArray: any[][] = [];

        perspectives.forEach((perspective) => {
          const entity = getEntityInfo(perspective);
          const modelData = entity.digitalTwin?.modelData;
          const score = modelData?.score ?? 0.5; // Default to middle if no score

          xValues.push(perspective.x);
          yValues.push(perspective.y);
          scores.push(score);
          textLabels.push(entity.name);
          customDataArray.push([
            entity.id,
            entity.name,
            score,
          ]);
        });

        return [
          {
            x: xValues,
            y: yValues,
            type: 'scatter' as const,
            mode: 'markers' as const,
            marker: {
              color: scores,
              colorscale: [
                [0, '#d73027'], // Red for low scores
                [0.25, '#fc8d59'],
                [0.5, '#fee08b'], // Yellow for medium scores
                [0.75, '#91cf60'],
                [1, '#1a9850'], // Green for high scores
              ],
              size: 10,
              showscale: true,
              colorbar: {
                title: {
                  text: 'Score',
                  side: 'right',
                },
                thickness: 15,
                len: 0.5,
                x: 1.02,
                xanchor: 'left',
                tickmode: 'linear',
                tick0: 0,
                dtick: 0.2,
                tickformat: '.1f',
              },
              cmin: 0,
              cmax: 1,
            },
            text: textLabels,
            customdata: customDataArray,
            hovertemplate:
              '<b>%{customdata[1]}</b><br><br>' +
              '<b>Score:</b> %{customdata[2]:.3f}<br><extra></extra>',
            showlegend: false,
          },
        ];
      }

      // Default behavior for non-Evaluation projects or subtopic views
      const scatterData = perspectives.map((perspective) => {
        const entity = getEntityInfo(perspective);
        const isIndividuallySelected = selectedTwinNamesMap.has(entity.name);
        const twinColor = selectedTwinColorMap.get(entity.id);
        const congressData = entity.digitalTwin?.congressData;
        const earlyStageVcData = entity.digitalTwin?.earlyStageVcData;
        const moviesSimpleData = entity.digitalTwin?.moviesSimpleData;
        const fortuneCeoData = entity.digitalTwin?.fortuneCeoData;
        const modelData = entity.digitalTwin?.modelData;
        const sendrilData = entity.digitalTwin?.sendrilData;
        const moviesMediumFullData =
          entity.digitalTwin?.moviesMediumFullData;
        const matchesDemographic =
          selectedDemographics && selectedDemographics.size > 0
            ? hasDemographics(
                selectedDemographics,
                congressData ?? undefined,
                moviesMediumFullData ?? undefined,
                moviesSimpleData ?? undefined,
                earlyStageVcData ?? undefined,
                fortuneCeoData ?? undefined,
                modelData ?? undefined
              )
            : false;

        // Determine marker color:
        // - Individually selected personas get their assigned color
        // - Demographic matches get green (same color for all)
        // - Default is blue
        let markerColor = HelivanColor.text.blueDark;
        if (isIndividuallySelected && twinColor) {
          markerColor = twinColor;
        } else if (matchesDemographic) {
          markerColor = '#52d152'; // Green for demographic matches
        }

        const isHighlighted = isIndividuallySelected || matchesDemographic;

        // For custom agents, use simple customdata with just id and name
        const dtType = entity.digitalTwin?.type;

        return {
          x: [perspective.x],
          y: [perspective.y],
          type: 'scatter' as const,
          mode: 'markers' as const,
          marker: {
            color: markerColor,
            opacity:
              selectedTwins.size === 0 &&
              (!selectedDemographics || selectedDemographics.size === 0)
                ? 1
                : isHighlighted
                  ? 1
                  : 0.1,
            size: isIndividuallySelected ? 12 : matchesDemographic ? 10 : undefined,
          },
          text: [entity.name],
          showlegend: false,
          customdata:
            entity.isCustomAgent
              ? ([[entity.id, entity.name]] as any[][])
              : dtType === DtType.Congressperson && congressData
                ? ([
                    [
                      entity.id,
                      entity.name,
                      congressData.party,
                      congressData.state,
                      congressData.chamber,
                    ],
                  ] as any[][])
                : dtType === DtType.EarlyStageVcs && earlyStageVcData
                  ? ([
                      [
                        entity.id,
                        entity.name,
                        earlyStageVcData.firm,
                        earlyStageVcData.title,
                        earlyStageVcData.location ?? 'not found',
                      ],
                    ] as any[][])
                  : dtType === DtType.FortuneCeos && fortuneCeoData
                    ? ([
                        [
                          entity.id,
                          entity.name,
                          fortuneCeoData.company,
                          fortuneCeoData.industry,
                        ],
                      ] as any[][])
                    : dtType === DtType.Model && modelData
                      ? ([
                          [
                            entity.id,
                            entity.name,
                            modelData.score,
                          ],
                        ] as any[][])
                      : (dtType === DtType.MoviesFull ||
                            dtType === DtType.MoviesMedium) &&
                          moviesMediumFullData
                        ? ([
                            [
                              entity.id,
                              entity.name,
                              moviesMediumFullData.age,
                              moviesMediumFullData.gender,
                              moviesMediumFullData.race,
                              moviesMediumFullData.locationType,
                            ],
                          ] as any[][])
                        : dtType === DtType.MoviesSimple && moviesSimpleData
                          ? ([
                              [
                                entity.id,
                                entity.name,
                                moviesSimpleData.age,
                                moviesSimpleData.gender,
                              ],
                            ] as any[][])
                          : dtType === DtType.Sendril && sendrilData
                            ? ([
                                [
                                  entity.id,
                                  entity.name,
                                  sendrilData.twitterHandle,
                                  sendrilData.tiktokHandle,
                                  sendrilData.instagramHandle,
                                  sendrilData.youtubeHandle,
                                  sendrilData.facebookHandle,
                                  sendrilData.substackHandle,
                                ],
                              ] as any[][])
                            : ([[entity.id, entity.name]] as any[][]),
          hovertemplate:
            entity.isCustomAgent
              ? '<b>%{customdata[1]}</b><br><br><extra></extra>'
              : dtType === DtType.Congressperson && congressData
                ? '<b>%{customdata[1]}</b><br><br>' +
                  '<b>Party:</b> %{customdata[2]}<br>' +
                  '<b>State:</b> %{customdata[3]}<br>' +
                  '<b>Chamber:</b> %{customdata[4]}<br><extra></extra>'
                : dtType === DtType.EarlyStageVcs && earlyStageVcData
                  ? '<b>%{customdata[1]}</b><br><br>' +
                    `${earlyStageVcData.firm ? '<b>Firm:</b> %{customdata[2]}<br>' : ''}` +
                    `${earlyStageVcData.title ? '<b>Position:</b> %{customdata[3]}<br>' : ''}` +
                    `${earlyStageVcData.location ? '<b>Location:</b> %{customdata[4]}<br><extra></extra>' : ''}`
                  : dtType === DtType.MoviesFull ||
                      (dtType === DtType.MoviesMedium && moviesMediumFullData)
                    ? '<b>Age:</b> %{customdata[2]}<br>' +
                      '<b>Gender:</b> %{customdata[3]}<br>' +
                      '<b>Race:</b> %{customdata[4]}<br>' +
                      '<b>Location:</b> %{customdata[5]}<br><extra></extra>'
                    : dtType === DtType.MoviesSimple && moviesSimpleData
                      ? '<b>Age:</b> %{customdata[2]}<br>' +
                        '<b>Gender:</b> %{customdata[3]}<br><extra></extra>'
                      : dtType === DtType.FortuneCeos && fortuneCeoData
                        ? '<b>%{customdata[1]}</b><br><br>' +
                          '<b>Company:</b> %{customdata[2]}<br>' +
                          '<b>Industry:</b> %{customdata[3]}<br><extra></extra>'
                        : dtType === DtType.Model && modelData
                          ? '<b>%{customdata[1]}</b><br><br>' +
                            '<b>Score:</b> %{customdata[2]}<br><extra></extra>'
                          : '<b>%{customdata[1]}</b><br><br><extra></extra>',
        };
      });

      // Movement visualization is now handled outside of Plotly
      // Keeping this empty array for potential future use
      const movementTraces: any[] = [];

      // Add KDE traces if we're in subtopic mode and have KDE data
      if (type === 'subtopic' && kdeData) {
        const kdeTraces = [];
        const maxScaleFactor = 0.3; // Maximum height for KDE curves

        // Add the overall KDE curve (if available)
        if (kdeData.all) {
          const maxKdeAll = Math.max(...kdeData.all.y);
          const scaleFactorAll = maxScaleFactor / maxKdeAll;

          kdeTraces.push({
            x: kdeData.all.x,
            y: kdeData.all.y.map((y) => y * scaleFactorAll),
            type: 'scatter' as const,
            mode: 'lines' as const,
            line: {
              shape: 'spline' as const,
              color: HelivanColor.text.blueDark,
              width: 2,
            },
            // fill: 'tozeroy',
            fillcolor: `rgba(70, 130, 180, 0.2)`,
            hoverinfo: 'none' as const,
            showlegend:
              selectedDemographics && selectedDemographics.size > 0
                ? true
                : false, // Show legend if demographic filter is active
            name: 'All',
            opacity:
              selectedDemographics && selectedDemographics.size > 0 ? 0.7 : 1, // Slightly fade the overall KDE when demographic is active
          });
        }

        // Add demographic KDE curves if demographic filter is active
        if (
          selectedDemographics &&
          selectedDemographics.size > 0 &&
          kdeData.withDemographic
        ) {
          // Get the maximum density value across all KDEs for consistent scaling
          const maxDensities = [
            kdeData.all ? Math.max(...kdeData.all.y) : 0,
            kdeData.withDemographic
              ? Math.max(...kdeData.withDemographic.y)
              : 0,
            kdeData.withoutDemographic
              ? Math.max(...kdeData.withoutDemographic.y)
              : 0,
          ];
          const maxKdeValue = Math.max(...maxDensities);
          const scaleFactor = maxScaleFactor / maxKdeValue;

          // Add KDE for perspectives with the demographic
          kdeTraces.push({
            x: kdeData.withDemographic.x,
            y: kdeData.withDemographic.y.map((y) => y * scaleFactor),
            type: 'scatter' as const,
            mode: 'lines' as const,
            line: {
              shape: 'spline' as const,
              color: '#52d152', // Match the demographic marker color
              width: 2,
            },
            fillcolor: 'rgba(245, 170, 66, 0.2)',
            hoverinfo: 'none' as const,
            showlegend: true,
            name: getDemographicsText(), // Use helper function to get display text
          });

          // Add KDE for perspectives without the demographic (if we have enough data points)
          if (
            kdeData.withoutDemographic &&
            groupedPerspectives.withoutDemographic.length >= 10
          ) {
            kdeTraces.push({
              x: kdeData.withoutDemographic.x,
              y: kdeData.withoutDemographic.y.map((y) => y * scaleFactor),
              type: 'scatter' as const,
              mode: 'lines' as const,
              line: {
                shape: 'spline' as const,
                color: '#7ea4fc',
                width: 1.5,
                dash: 'dot' as const, // Use dotted line for contrast
              },
              fillcolor: 'rgba(70, 130, 180, 0.1)',
              hoverinfo: 'none' as const,
              showlegend: true,
              name: `Others`,
              opacity: 0.7, // Make this slightly transparent
            });
          }
        }

        return [...scatterData, ...movementTraces, ...kdeTraces];
      }

      return [...scatterData, ...movementTraces];
    }, [
      perspectives,
      selectedTwinNamesMap,
      selectedTwinColorMap,
      filteringOn,
      selectedTwins.size,
      selectedDemographics,
      type,
      kdeData,
      groupedPerspectives,
      hasDemographics,
      getDemographicsText,
      showMovement,
      comparisonPerspectives,
      projectType,
    ]);

    const layout = useMemo(() => {
      const layoutConfig: Partial<Layout> = {
        // Fluid width: fill the container (capped to 700px on desktop by the wrapper) so the
        // chart scales down on phones instead of overflowing.
        height: type === 'topic' ? 450 : 200,
        autosize: true,
        margin: {
          l: 40,
          r: projectType === ProjectType.Evaluation && type === 'topic' ? 100 : 40, // Extra space for colorbar
          b: 40,
          t: 50,
          pad: 2,
        },
        title: { text: topic },
        xaxis: {
          showticklabels: type === 'subtopic' ? true : false,
          tickfont: isMobile ? { size: 9 } : undefined,
          fixedrange: true,
          range: type === 'subtopic' ? [-3, 3] : undefined,
          autorange: type === 'subtopic' ? false : undefined,
          showline: type === 'subtopic' ? true : false, // Hide axis line for topic mode
          zeroline: type === 'subtopic' ? true : false, // Hide zero line for topic mode
          showgrid: false, // Hide vertical grid lines
        },
        yaxis: {
          showticklabels: false,
          fixedrange: true,
          range: type === 'subtopic' ? [-0.02, 0.32] : undefined, // Set fixed range for KDE y-axis
          zeroline: type === 'subtopic' ? true : false, // Hide zero line for topic mode
          showline: type === 'subtopic' ? true : false, // Hide axis line for topic mode
          showgrid: false,
          title: undefined,
        },
        clickmode: 'event',
        // Add legend configuration when we have demographic KDEs
        showlegend:
          type === 'subtopic' &&
          selectedDemographics !== undefined &&
          selectedDemographics.size > 0,
        legend:
          type === 'subtopic' &&
          selectedDemographics !== undefined &&
          selectedDemographics.size > 0
            ? {
                x: 1,
                y: 1,
                xanchor: 'right' as const,
                yanchor: 'top' as const,
                bgcolor: 'rgba(255, 255, 255, 0.7)',
                bordercolor: '#ddd',
                borderwidth: 1,
                font: { size: 10 },
              }
            : undefined,
      };

      // Build annotations array
      const annotations: any[] = [];

      if (leftTitle && rightTitle) {
        annotations.push(
          {
            text: leftTitle,
            xref: 'paper' as const,
            yref: 'paper' as const,
            x:
              leftTitle.length > 30
                ? 0.25
                : leftTitle.length > 21
                  ? 0.21
                  : leftTitle.length > 16
                    ? 0.15
                    : 0.1,
            xanchor: 'right' as const,
            y: 1,
            yanchor: 'bottom' as const,
            showarrow: false,
            font: {
              size: 12,
            },
          },
          {
            text: rightTitle,
            xref: 'paper' as const,
            yref: 'paper' as const,
            x:
              rightTitle.length > 30
                ? 0.7
                : rightTitle.length > 20
                  ? 0.75
                  : rightTitle.length > 16
                    ? 0.82
                    : 0.9,
            xanchor: 'left' as const,
            y: 1,
            yanchor: 'bottom' as const,
            showarrow: false,
            font: {
              size: 12,
            },
          }
        );
      }

      // Removed Plotly annotation - will display shift summary outside of Plotly

      if (annotations.length > 0) {
        layoutConfig.annotations = annotations;
      }

      return layoutConfig;
    }, [
      type,
      topic,
      leftTitle,
      rightTitle,
      selectedDemographics,
      shiftSummary,
      projectType,
      isMobile,
    ]);

    // Update the config to improve responsiveness
    const config = useMemo(
      () => ({
        displayModeBar: false,
        responsive: true,
        staticPlot: false,
      }),
      []
    );

    // Throttle click handler to prevent rapid multiple selections
    const [lastClickTime, setLastClickTime] = useState(0);
    const handleClick = useCallback(
      (event: any) => {
        const now = Date.now();
        if (now - lastClickTime < 300) return; // Prevent double-clicks
        setLastClickTime(now);

        // Only process clicks for scatter points, not the KDE curve
        if (!event.points[0].customdata) return;

        const point = event.points[0];
        const customData = point.customdata as unknown as string[];
        const id = customData[0];
        const name = customData.length > 1 ? customData[1] : undefined;

        setDigitalTwinId(id);

        if (name && id) {
          // Check if Ctrl/Cmd key is held for multi-select
          const isMultiSelect = event.event?.ctrlKey || event.event?.metaKey;

          if (isMultiSelect) {
            // Toggle selection
            setSelectedTwins((prev) => {
              const newSet = new Set(prev);
              const existingTwin = Array.from(newSet).find(t => t.id === id);
              if (existingTwin) {
                newSet.delete(existingTwin);
              } else {
                // Limit to 8 selections for color variety
                if (newSet.size < 8) {
                  newSet.add({ id, name });
                }
              }
              return newSet;
            });
          } else {
            // Single select - replace selection
            setSelectedTwins(new Set([{ id, name }]));
          }
        }
      },
      [setDigitalTwinId, setSelectedTwins, lastClickTime]
    );

    // If no timeline projects, just show the All Perspectives view without tabs
    if (!timelineProjects || timelineProjects.length === 0) {
      return (
        <div style={{ width: '100%' }}>
          <div
            style={{
              height: type === 'topic' ? '450px' : '200px',
              width: '100%',
              maxWidth: isMobile ? undefined : '1000px',
              margin: '0 auto',
            }}
          >
            <Plot
              ref={plotRef}
              data={data}
              layout={layout}
              config={config}
              onClick={handleClick}
              style={{
                width: '100%',
                height: '100%',
              }}
              useResizeHandler={true}
            />
          </div>
          {/* Only show movement summary on "All Perspectives" tab */}
          {shiftSummary && shiftSummary.total > 0 && (
            <div
              style={{
                marginTop: '8px',
                padding: '12px',
                backgroundColor: '#f8f9fa',
                border: '1px solid #dee2e6',
                borderRadius: '6px',
                fontSize: '14px',
              }}
            >
              <div style={{ fontWeight: 600, marginBottom: '8px' }}>
                Movement Summary ({shiftSummary.matchedCount} perspectives)
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns:
                    'repeat(auto-fit, minmax(150px, 1fr))',
                  gap: '8px',
                }}
              >
                {shiftSummary.rightPct >= 1 && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}
                  >
                    <span style={{ fontSize: '18px' }}>→</span>
                    <span>
                      {shiftSummary.rightPct.toFixed(1)}% shift right (
                      {shiftSummary.rightShift})
                    </span>
                  </div>
                )}
                {shiftSummary.leftPct >= 1 && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}
                  >
                    <span style={{ fontSize: '18px' }}>←</span>
                    <span>
                      {shiftSummary.leftPct.toFixed(1)}% shift left (
                      {shiftSummary.leftShift})
                    </span>
                  </div>
                )}
                {shiftSummary.upPct >= 1 && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}
                  >
                    <span style={{ fontSize: '18px' }}>↑</span>
                    <span>
                      {shiftSummary.upPct.toFixed(1)}% shift up (
                      {shiftSummary.upShift})
                    </span>
                  </div>
                )}
                {shiftSummary.downPct >= 1 && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}
                  >
                    <span style={{ fontSize: '18px' }}>↓</span>
                    <span>
                      {shiftSummary.downPct.toFixed(1)}% shift down (
                      {shiftSummary.downShift})
                    </span>
                  </div>
                )}
                {shiftSummary.noChangePct >= 1 && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}
                  >
                    <span style={{ fontSize: '18px' }}>⊙</span>
                    <span>
                      {shiftSummary.noChangePct.toFixed(1)}% minimal change
                      ({shiftSummary.noChange})
                    </span>
                  </div>
                )}
              </div>
              {shiftSummary.medianMagnitude > 0 && (
                <div
                  style={{
                    marginTop: '8px',
                    fontSize: '12px',
                    color: '#6c757d',
                  }}
                >
                  Median movement: {shiftSummary.medianMagnitude.toFixed(3)}{' '}
                  | Max: {shiftSummary.maxMagnitude.toFixed(3)}
                </div>
              )}
              {shiftSummary.significantMovers &&
                shiftSummary.significantMovers.length > 0 && (
                  <div style={{ marginTop: '12px' }}>
                    <div
                      onClick={() =>
                        setSignificantShiftsExpanded(
                          !significantShiftsExpanded
                        )
                      }
                      style={{
                        fontWeight: 600,
                        marginBottom: significantShiftsExpanded
                          ? '8px'
                          : '0',
                        fontSize: '13px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        userSelect: 'none',
                      }}
                    >
                      <span
                        style={{
                          transition: 'transform 0.2s',
                          transform: significantShiftsExpanded
                            ? 'rotate(90deg)'
                            : 'rotate(0deg)',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <IoChevronForward
                          color={HelivanColor.text.blueMid}
                          size={14}
                        />
                      </span>
                      Significant Shifts (≥0.5 points):{' '}
                      {shiftSummary.significantMovers.length}
                    </div>
                    {significantShiftsExpanded && (
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '6px',
                        }}
                      >
                        {shiftSummary.significantMovers.map(
                          (mover, idx) => {
                            const directionIcon =
                              mover.direction === 'right'
                                ? '→'
                                : mover.direction === 'left'
                                  ? '←'
                                  : mover.direction === 'up'
                                    ? '↑'
                                    : '↓';

                            return (
                              <div
                                key={idx}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                  fontSize: '12px',
                                  padding: '4px 8px',
                                  backgroundColor: '#fff',
                                  border: '1px solid #e0e0e0',
                                  borderRadius: '4px',
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: '16px',
                                    minWidth: '20px',
                                  }}
                                >
                                  {directionIcon}
                                </span>
                                <span style={{ flex: 1, fontWeight: 500 }}>
                                  {mover.name}
                                </span>
                                <span
                                  style={{
                                    fontSize: '11px',
                                    color: '#6c757d',
                                    fontFamily: 'monospace',
                                  }}
                                >
                                  {mover.magnitude.toFixed(2)}
                                </span>
                              </div>
                            );
                          }
                        )}
                      </div>
                    )}
                  </div>
                )}
            </div>
          )}
        </div>
      );
    }

    // If timelineProjects exists, show the tabular view
    return (
      <div style={{ width: '100%' }}>
        <Tabs index={tabIndex} onChange={handleTabChange} variant="enclosed">
          <TabList>
            <Tab isDisabled={!canShowTimeline}>
              <HelivanText variant="smallBody">
                Timeline
                {!hasSelectedTwins && !hasDemographicFilter && ' (select personas)'}
                {hasSelectedTwins && selectedTwins.size > 1 && ` (${selectedTwins.size})`}
                {!hasSelectedTwins && hasDemographicFilter && ` (${demographicMatchedTwins.length} matching)`}
              </HelivanText>
            </Tab>
            <Tab>
              <HelivanText variant="smallBody">All Perspectives</HelivanText>
            </Tab>
          </TabList>

          <TabPanels>
            {/* Tab 1: Timeline (supports multiple personas) */}
            <TabPanel p={0} pt={4}>
              {/* Single explorable - show placeholder since there's only 1 data point */}
              {timelineProjects && timelineProjects.length === 1 ? (
                <Flex
                  p={8}
                  bg="#f8f9fa"
                  borderRadius="8px"
                  border="1px solid #dee2e6"
                  alignItems="center"
                  justifyContent="center"
                  flexDirection="column"
                  gap={3}
                  minHeight="200px"
                >
                  <Box
                    as="svg"
                    width="48px"
                    height="48px"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={HelivanColor.text.blueMid}
                    strokeWidth="1.5"
                  >
                    <path d="M3 3v18h18" strokeLinecap="round" strokeLinejoin="round" />
                    <circle cx="9" cy="14" r="2" fill={HelivanColor.text.blueMid} stroke="none" />
                  </Box>
                  <HelivanText variant="largeBody" colorOverride={HelivanColor.text.blueDark} textAlign="center">
                    Timeline tracking started
                  </HelivanText>
                  <HelivanText variant="smallBody" colorOverride={HelivanColor.text.blueMid} textAlign="center" maxWidth="400px">
                    This is the first data point. As more updates are generated, you'll see trends and changes over time.
                  </HelivanText>
                </Flex>
              ) : canShowTimeline && (selectedTwinIds.length > 0 || demographicMatchedTwins.length > 0) ? (
                <div>
                  {/* Legend showing selected personas or demographic filter */}
                  {selectedTwins.size > 0 ? (
                    <HStack mb={3} spacing={4} flexWrap="wrap">
                      {Array.from(selectedTwins).map((twin, idx) => (
                        <HStack key={twin.id} spacing={2}>
                          <Box
                            width="12px"
                            height="12px"
                            borderRadius="50%"
                            bg={PERSONA_COLORS[idx % PERSONA_COLORS.length]}
                          />
                          <HelivanText variant="smallBody" colorOverride={HelivanColor.text.blueDark}>
                            {twin.name}
                          </HelivanText>
                        </HStack>
                      ))}
                      {timelineData.size > 1 && (
                        <HStack spacing={2}>
                          <Box width="12px" height="3px" bg="#333" />
                          <HelivanText variant="smallBody" colorOverride={HelivanColor.text.blueDark}>
                            Average
                          </HelivanText>
                        </HStack>
                      )}
                      {changeDetectionEnabled && (
                        <HStack spacing={1}>
                          <Box width="16px" height="10px" bg="rgba(0,71,125,0.15)" borderRadius="2px" />
                          <HelivanText variant="tinyBody" colorOverride={HelivanColor.text.blueMid}>
                            ±{sensitivityStdDevs}σ confidence region
                          </HelivanText>
                          <Tooltip label={CONTROL_BAND_HELP} placement="top" hasArrow>
                            <span>
                              <Icon as={RiInformationLine} boxSize="13px" color={HelivanColor.text.blueMid} cursor="help" />
                            </span>
                          </Tooltip>
                        </HStack>
                      )}
                    </HStack>
                  ) : hasDemographicFilter && (
                    <HStack mb={3} spacing={4} flexWrap="wrap">
                      <HStack spacing={2}>
                        <Box width="12px" height="12px" borderRadius="50%" bg="#52d152" />
                        <HelivanText variant="smallBody" colorOverride={HelivanColor.text.blueDark}>
                          {getDemographicsText()} ({demographicMatchedTwins.length} personas)
                        </HelivanText>
                      </HStack>
                      {timelineData.size > 1 && (
                        <HStack spacing={2}>
                          <Box width="12px" height="3px" bg="#333" />
                          <HelivanText variant="smallBody" colorOverride={HelivanColor.text.blueDark}>
                            Average
                          </HelivanText>
                        </HStack>
                      )}
                      {changeDetectionEnabled && (
                        <HStack spacing={1}>
                          <Box width="16px" height="10px" bg="rgba(82,209,82,0.15)" borderRadius="2px" />
                          <HelivanText variant="tinyBody" colorOverride={HelivanColor.text.blueMid}>
                            ±{sensitivityStdDevs}σ confidence region
                          </HelivanText>
                          <Tooltip label={CONTROL_BAND_HELP} placement="top" hasArrow>
                            <span>
                              <Icon as={RiInformationLine} boxSize="13px" color={HelivanColor.text.blueMid} cursor="help" />
                            </span>
                          </Tooltip>
                        </HStack>
                      )}
                    </HStack>
                  )}

                  {loadingTimeline ? (
                    <Flex
                      p={8}
                      bg="#f8f9fa"
                      borderRadius="8px"
                      border="1px solid #dee2e6"
                      alignItems="center"
                      justifyContent="center"
                      flexDirection="column"
                      gap={3}
                      minHeight="200px"
                    >
                      <Spinner
                        size="lg"
                        color={HelivanColor.text.helivanBlue}
                        thickness="3px"
                      />
                      <HelivanText variant="smallBody" colorOverride={HelivanColor.text.blueMid}>
                        Building your timeline...
                      </HelivanText>
                    </Flex>
                  ) : timelineData.size > 0 ? (
                    <Flex justifyContent="center" width="full">
                      <div
                        style={{
                          height: type === 'topic' ? '450px' : '250px',
                          width: '100%',
                          // Use the available width, but cap it so a wide monitor doesn't
                          // stretch the chart into a short, squished band. Min-width keeps
                          // it scrollable rather than crushed on phones.
                          maxWidth: isMobile ? undefined : '1000px',
                          minWidth: isMobile ? '640px' : undefined,
                          overflowX: isMobile ? 'auto' : 'visible',
                        }}
                      >
                      {(() => {
                        // Get all unique dates and sort them
                        const allDates = new Set<string>();
                        Array.from(timelineData.values()).forEach(positions => {
                          positions.forEach(p => allDates.add(p.dateCreated));
                        });
                        const sortedDates = Array.from(allDates).sort(
                          (a, b) => new Date(a).getTime() - new Date(b).getTime()
                        );
                        const dateToIndex = new Map(sortedDates.map((d, i) => [d, i]));
                        // Label resolution (day, + time when sub-daily) is shared with the
                        // Inspect "compare with" picker so axis ticks and version labels agree.
                        const dateLabels = sortedDates.map(d => formatVersionLabel(sortedDates, d));
                        // Thin the x-axis labels so many check-ins don't overlap into an
                        // unreadable smear — show at most ~8 (always incl. first & last).
                        const MAX_TICKS = 8;
                        const tickStride = Math.max(1, Math.ceil(sortedDates.length / MAX_TICKS));
                        const labeledIdx = sortedDates
                          .map((_, i) => i)
                          .filter((i) => i % tickStride === 0 || i === sortedDates.length - 1);
                        const labeledText = labeledIdx.map((i) => dateLabels[i]);
                        const fmtHoverDate = (val: string | number | Date) =>
                          formatVersionLabel(sortedDates, typeof val === 'string' ? val : new Date(val).toISOString());

                        // Calculate error regions for each persona
                        const errorRegionTraces: any[] = [];
                        const projectionTraces: any[] = [];
                        const changeDetectedTraces: any[] = [];

                        if (changeDetectionEnabled) {
                          Array.from(timelineData.entries()).forEach(([twinId, positions], idx) => {
                            const yValues = positions.map(p => p.x);
                            const xIndices = positions.map(p => dateToIndex.get(p.dateCreated) ?? 0);
                            const color = positions[0]?.color || HelivanColor.text.blueDark;

                            // Only show error regions after burn-in period
                            if (yValues.length >= burnInPeriod) {
                              const stats = calculateMovingStats(yValues, burnInPeriod, sensitivityStdDevs);

                              // Find points where change was detected (outside bounds)
                              const changePoints: { x: number; y: number; name: string; date: string }[] = [];
                              for (let i = burnInPeriod; i < yValues.length; i++) {
                                const prevStats = stats[i - 1]; // Use previous point's stats to check current
                                const currentValue = yValues[i];
                                if (currentValue > prevStats.upperBound || currentValue < prevStats.lowerBound) {
                                  changePoints.push({
                                    x: xIndices[i],
                                    y: currentValue,
                                    name: positions[i]?.name || twinId,
                                    date: positions[i]?.dateCreated || '',
                                  });
                                }
                              }

                              // Add change detection markers
                              if (changePoints.length > 0) {
                                changeDetectedTraces.push({
                                  x: changePoints.map(p => p.x),
                                  y: changePoints.map(p => p.y),
                                  type: 'scatter' as const,
                                  mode: 'markers' as const,
                                  name: `${positions[0]?.name || twinId} (changes)`,
                                  marker: {
                                    size: 14,
                                    symbol: 'star',
                                    color: '#FF6B35',
                                    line: { color: '#fff', width: 1 },
                                  },
                                  text: changePoints.map(p => {
                                    return `⚠ Change detected: ${p.name}<br>${fmtHoverDate(p.date)}`;
                                  }),
                                  hovertemplate: '<b>%{text}</b><br>Position: %{y:.2f}<extra></extra>',
                                  showlegend: false,
                                });
                              }

                              // Control band = baseline (mean) ± sensitivity·σ.
                              // Render as a proper control chart — centerline + both
                              // limit lines + shaded region — so it reads clearly as a
                              // symmetric band rather than a vague one-sided fill.
                              const clampedUpper = stats.map(s => Math.min(s.upperBound, 3));
                              const clampedLower = stats.map(s => Math.max(s.lowerBound, -3));

                              // Upper bound (invisible anchor for the fill)
                              errorRegionTraces.push({
                                x: xIndices,
                                y: clampedUpper,
                                type: 'scatter' as const,
                                mode: 'lines' as const,
                                line: { color: 'transparent', width: 0 },
                                showlegend: false,
                                hoverinfo: 'skip' as const,
                              });

                              // Lower bound, filled up to the upper bound
                              errorRegionTraces.push({
                                x: xIndices,
                                y: clampedLower,
                                type: 'scatter' as const,
                                mode: 'lines' as const,
                                fill: 'tonexty' as const,
                                fillcolor: colorWithAlpha(color, 0.18),
                                line: { color: 'transparent', width: 0 },
                                showlegend: false,
                                hoverinfo: 'skip' as const,
                              });

                              // Visible upper & lower control limits
                              errorRegionTraces.push({
                                x: xIndices,
                                y: clampedUpper,
                                type: 'scatter' as const,
                                mode: 'lines' as const,
                                line: { color: colorWithAlpha(color, 0.45), width: 1, dash: 'dot' as const },
                                showlegend: false,
                                hovertemplate: 'Upper limit: %{y:.2f}<extra></extra>',
                              });
                              errorRegionTraces.push({
                                x: xIndices,
                                y: clampedLower,
                                type: 'scatter' as const,
                                mode: 'lines' as const,
                                line: { color: colorWithAlpha(color, 0.45), width: 1, dash: 'dot' as const },
                                showlegend: false,
                                hovertemplate: 'Lower limit: %{y:.2f}<extra></extra>',
                              });

                              // Centerline (baseline mean) — anchors the symmetry
                              errorRegionTraces.push({
                                x: xIndices,
                                y: stats.map(s => s.mean),
                                type: 'scatter' as const,
                                mode: 'lines' as const,
                                line: { color: colorWithAlpha(color, 0.6), width: 1, dash: 'dash' as const },
                                showlegend: false,
                                hovertemplate: 'Baseline: %{y:.2f}<extra></extra>',
                              });

                              // Project future bounds (2 points)
                              if (yValues.length >= 3) {
                                const projection = projectFutureBounds(
                                  yValues,
                                  xIndices,
                                  2,
                                  Math.min(burnInPeriod, yValues.length),
                                  sensitivityStdDevs
                                );

                                if (projection.dates.length > 0) {
                                  const projX = [...xIndices.slice(-1), ...projection.dates];
                                  const projUpper = [stats[stats.length - 1].upperBound, ...projection.upper].map(v => Math.min(v, 3));
                                  const projLower = [stats[stats.length - 1].lowerBound, ...projection.lower].map(v => Math.max(v, -3));

                                  // Projected upper bound (dotted line)
                                  projectionTraces.push({
                                    x: projX,
                                    y: projUpper,
                                    type: 'scatter' as const,
                                    mode: 'lines' as const,
                                    line: { color: colorWithAlpha(color, 0.4), width: 1, dash: 'dot' as const },
                                    showlegend: false,
                                    hoverinfo: 'skip' as const,
                                  });

                                  // Projected lower bound with fill (dotted line)
                                  projectionTraces.push({
                                    x: projX,
                                    y: projLower,
                                    type: 'scatter' as const,
                                    mode: 'lines' as const,
                                    fill: 'tonexty' as const,
                                    fillcolor: colorWithAlpha(color, 0.06),
                                    line: { color: colorWithAlpha(color, 0.4), width: 1, dash: 'dot' as const },
                                    showlegend: false,
                                    hoverinfo: 'skip' as const,
                                  });

                                  // Projected center line
                                  projectionTraces.push({
                                    x: projX,
                                    y: [yValues[yValues.length - 1], ...projection.projected],
                                    type: 'scatter' as const,
                                    mode: 'lines' as const,
                                    line: { color: color, width: 1, dash: 'dash' as const },
                                    showlegend: false,
                                    hovertemplate: 'Projected: %{y:.2f}<extra></extra>',
                                  });
                                }
                              }
                            }
                          });
                        }

                        return (
                          <Plot
                            data={[
                              // Error regions (rendered first, behind the lines)
                              ...errorRegionTraces,
                              // Individual persona timelines
                              ...Array.from(timelineData.entries()).map(([twinId, positions]) => ({
                                x: positions.map((p) => dateToIndex.get(p.dateCreated) ?? 0),
                                y: positions.map((p) => p.x),
                                type: 'scatter' as const,
                                mode: 'lines+markers' as const,
                                name: positions[0]?.name || twinId,
                                line: { color: positions[0]?.color || HelivanColor.text.blueDark, width: 2 },
                                marker: { size: 8, color: positions[0]?.color || HelivanColor.text.blueDark },
                                text: positions.map((p) => {
                                  return `${p.name}: ${fmtHoverDate(p.dateCreated)}`;
                                }),
                                hovertemplate: '<b>%{text}</b><br>Position: %{y:.2f}<extra></extra>',
                              })),
                              // Projected future regions
                              ...projectionTraces,
                              // Change detection markers (rendered on top)
                              ...changeDetectedTraces,
                              // Average timeline (only if multiple personas selected)
                              ...(timelineData.size > 1 ? [(() => {
                                const datePositions = new Map<string, number[]>();
                                Array.from(timelineData.values()).forEach(positions => {
                                  positions.forEach(p => {
                                    if (!datePositions.has(p.dateCreated)) {
                                      datePositions.set(p.dateCreated, []);
                                    }
                                    datePositions.get(p.dateCreated)!.push(p.x);
                                  });
                                });

                                const avgPositions = sortedDates.map(date => {
                                  const values = datePositions.get(date);
                                  return values ? values.reduce((sum, v) => sum + v, 0) / values.length : 0;
                                });

                                return {
                                  x: sortedDates.map((_, i) => i),
                                  y: avgPositions,
                                  type: 'scatter' as const,
                                  mode: 'lines' as const,
                                  name: 'Average',
                                  line: { color: '#333', width: 3, dash: 'dash' as const },
                                  text: sortedDates.map((date) => {
                                    return `Average: ${fmtHoverDate(date)}`;
                                  }),
                                  hovertemplate: '<b>%{text}</b><br>Avg Position: %{y:.2f}<extra></extra>',
                                };
                              })()] : []),
                            ]}
                            layout={{
                              height: type === 'topic' ? 450 : 250,
                              autosize: true,
                              // Phones: shrink the left margin (anchor labels) and use smaller,
                              // angled ticks so the fluid chart stays legible instead of crushed.
                              margin: { l: isMobile ? 90 : 180, r: isMobile ? 24 : 40, b: isMobile ? 70 : 60, t: 40, pad: 2 },
                              xaxis: {
                                tickmode: 'array' as const,
                                tickvals: changeDetectionEnabled && projectionTraces.length > 0
                                  ? [...labeledIdx, sortedDates.length, sortedDates.length + 1]
                                  : labeledIdx,
                                ticktext: changeDetectionEnabled && projectionTraces.length > 0
                                  ? [...labeledText, 'Future', '']
                                  : labeledText,
                                showticklabels: true,
                                tickfont: isMobile ? { size: 9 } : { size: 11 },
                                // Angle the date labels so many versions don't smush together.
                                tickangle: -30,
                                automargin: true,
                                fixedrange: true,
                                showline: true,
                                zeroline: false,
                                showgrid: true,
                                gridcolor: '#e0e0e0',
                                range: changeDetectionEnabled && projectionTraces.length > 0
                                  ? [-0.5, sortedDates.length + 1.5]
                                  : undefined,
                              },
                              yaxis: {
                                showticklabels: true,
                                tickfont: isMobile ? { size: 9 } : undefined,
                                fixedrange: true,
                                range: [-3, 3],
                                zeroline: true,
                                showline: true,
                                showgrid: true,
                                gridcolor: '#e0e0e0',
                              },
                              showlegend: false,
                              clickmode: 'none',
                              annotations: leftTitle && rightTitle ? [
                                { text: rightTitle, xref: 'paper' as const, yref: 'y' as const, x: -0.02, xanchor: 'right' as const, y: 3, yanchor: 'middle' as const, showarrow: false, font: { size: isMobile ? 9 : 11 } },
                                { text: leftTitle, xref: 'paper' as const, yref: 'y' as const, x: -0.02, xanchor: 'right' as const, y: -3, yanchor: 'middle' as const, showarrow: false, font: { size: isMobile ? 9 : 11 } },
                              ] : [
                                { text: 'Perspective', xref: 'paper' as const, yref: 'paper' as const, x: -0.02, xanchor: 'right' as const, y: 0.5, yanchor: 'middle' as const, showarrow: false, font: { size: 11 }, textangle: '-90' as any },
                              ],
                            }}
                            config={{ displayModeBar: false, responsive: true, staticPlot: false }}
                            style={{ width: '100%', height: '100%' }}
                          />
                        );
                      })()}
                      </div>
                    </Flex>
                  ) : (
                    <Flex
                      p={8}
                      bg="#f8f9fa"
                      borderRadius="8px"
                      border="1px solid #dee2e6"
                      alignItems="center"
                      justifyContent="center"
                      flexDirection="column"
                      gap={3}
                      minHeight="200px"
                    >
                      <Spinner
                        size="lg"
                        color={HelivanColor.text.helivanBlue}
                        thickness="3px"
                      />
                      <HelivanText variant="smallBody" colorOverride={HelivanColor.text.blueMid}>
                        Building your timeline...
                      </HelivanText>
                    </Flex>
                  )}
                </div>
              ) : (
                <Box p={4} bg="#f8f9fa" borderRadius="6px" border="1px solid #dee2e6">
                  <HelivanText variant="smallBody" colorOverride="#6c757d">
                    Select personas from the "All Perspectives" tab to view their timeline.
                    Hold Ctrl/Cmd and click to select multiple.
                  </HelivanText>
                </Box>
              )}
            </TabPanel>

            {/* Tab 2: All Perspectives */}
            <TabPanel p={0} pt={4}>
              <div
                style={{
                  height: type === 'topic' ? '450px' : '200px',
                  minWidth: '100%',
                }}
              >
                <Plot
                  ref={plotRef}
                  data={data}
                  layout={layout}
                  config={config}
                  onClick={handleClick}
                  style={{
                    width: '100%',
                    height: '100%',
                  }}
                  useResizeHandler={true}
                />
              </div>
              {/* Only show movement summary on "All Perspectives" tab */}
              {shiftSummary && shiftSummary.total > 0 && (
                <div
                  style={{
                    marginTop: '8px',
                    padding: '12px',
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #dee2e6',
                    borderRadius: '6px',
                    fontSize: '14px',
                  }}
                >
                  <div style={{ fontWeight: 600, marginBottom: '8px' }}>
                    Movement Summary ({shiftSummary.matchedCount} perspectives)
                  </div>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns:
                        'repeat(auto-fit, minmax(150px, 1fr))',
                      gap: '8px',
                    }}
                  >
                    {shiftSummary.rightPct >= 1 && (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                        }}
                      >
                        <span style={{ fontSize: '18px' }}>→</span>
                        <span>
                          {shiftSummary.rightPct.toFixed(1)}% shift right (
                          {shiftSummary.rightShift})
                        </span>
                      </div>
                    )}
                    {shiftSummary.leftPct >= 1 && (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                        }}
                      >
                        <span style={{ fontSize: '18px' }}>←</span>
                        <span>
                          {shiftSummary.leftPct.toFixed(1)}% shift left (
                          {shiftSummary.leftShift})
                        </span>
                      </div>
                    )}
                    {shiftSummary.upPct >= 1 && (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                        }}
                      >
                        <span style={{ fontSize: '18px' }}>↑</span>
                        <span>
                          {shiftSummary.upPct.toFixed(1)}% shift up (
                          {shiftSummary.upShift})
                        </span>
                      </div>
                    )}
                    {shiftSummary.downPct >= 1 && (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                        }}
                      >
                        <span style={{ fontSize: '18px' }}>↓</span>
                        <span>
                          {shiftSummary.downPct.toFixed(1)}% shift down (
                          {shiftSummary.downShift})
                        </span>
                      </div>
                    )}
                    {shiftSummary.noChangePct >= 1 && (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                        }}
                      >
                        <span style={{ fontSize: '18px' }}>⊙</span>
                        <span>
                          {shiftSummary.noChangePct.toFixed(1)}% minimal change
                          ({shiftSummary.noChange})
                        </span>
                      </div>
                    )}
                  </div>
                  {shiftSummary.medianMagnitude > 0 && (
                    <div
                      style={{
                        marginTop: '8px',
                        fontSize: '12px',
                        color: '#6c757d',
                      }}
                    >
                      Median movement: {shiftSummary.medianMagnitude.toFixed(3)}{' '}
                      | Max: {shiftSummary.maxMagnitude.toFixed(3)}
                    </div>
                  )}
                  {shiftSummary.significantMovers &&
                    shiftSummary.significantMovers.length > 0 && (
                      <div style={{ marginTop: '12px' }}>
                        <div
                          onClick={() =>
                            setSignificantShiftsExpanded(
                              !significantShiftsExpanded
                            )
                          }
                          style={{
                            fontWeight: 600,
                            marginBottom: significantShiftsExpanded
                              ? '8px'
                              : '0',
                            fontSize: '13px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            userSelect: 'none',
                          }}
                        >
                          <span
                            style={{
                              transition: 'transform 0.2s',
                              transform: significantShiftsExpanded
                                ? 'rotate(90deg)'
                                : 'rotate(0deg)',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <IoChevronForward
                              color={HelivanColor.text.blueMid}
                              size={14}
                            />
                          </span>
                          Significant Shifts (≥0.5 points):{' '}
                          {shiftSummary.significantMovers.length}
                        </div>
                        {significantShiftsExpanded && (
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '6px',
                            }}
                          >
                            {shiftSummary.significantMovers.map(
                              (mover, idx) => {
                                const directionIcon =
                                  mover.direction === 'right'
                                    ? '→'
                                    : mover.direction === 'left'
                                      ? '←'
                                      : mover.direction === 'up'
                                        ? '↑'
                                        : '↓';

                                return (
                                  <div
                                    key={idx}
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '8px',
                                      fontSize: '12px',
                                      padding: '4px 8px',
                                      backgroundColor: '#fff',
                                      border: '1px solid #e0e0e0',
                                      borderRadius: '4px',
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontSize: '16px',
                                        minWidth: '20px',
                                      }}
                                    >
                                      {directionIcon}
                                    </span>
                                    <span style={{ flex: 1, fontWeight: 500 }}>
                                      {mover.name}
                                    </span>
                                    <span
                                      style={{
                                        fontSize: '11px',
                                        color: '#6c757d',
                                        fontFamily: 'monospace',
                                      }}
                                    >
                                      {mover.magnitude.toFixed(2)}
                                    </span>
                                  </div>
                                );
                              }
                            )}
                          </div>
                        )}
                      </div>
                    )}
                </div>
              )}
            </TabPanel>
          </TabPanels>
        </Tabs>
      </div>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison function to prevent unnecessary re-renders
    if (prevProps.perspectives !== nextProps.perspectives) {
      return false; // Re-render if perspectives change
    }

    if (prevProps.selectedTwins.size !== nextProps.selectedTwins.size) {
      return false; // Re-render if selection size changes
    }

    if (prevProps.topic !== nextProps.topic) {
      return false; // Re-render if topic changes
    }

    // Check if the selected twin changed
    if (
      prevProps.selectedTwins.size === 1 &&
      nextProps.selectedTwins.size === 1
    ) {
      const prevTwin = Array.from(prevProps.selectedTwins)[0];
      const nextTwin = Array.from(nextProps.selectedTwins)[0];

      if (prevTwin.id !== nextTwin.id) {
        return false; // Selected twin changed
      }
    }

    // Check if the selected demographics changed
    if (
      (!prevProps.selectedDemographics && nextProps.selectedDemographics) ||
      (prevProps.selectedDemographics && !nextProps.selectedDemographics) ||
      (prevProps.selectedDemographics &&
        nextProps.selectedDemographics &&
        prevProps.selectedDemographics.size !==
          nextProps.selectedDemographics.size)
    ) {
      return false; // Re-render if selectedDemographics changed
    }

    // If both have selectedDemographics and same size, check for content changes
    if (
      prevProps.selectedDemographics &&
      nextProps.selectedDemographics &&
      prevProps.selectedDemographics.size ===
        nextProps.selectedDemographics.size &&
      prevProps.selectedDemographics.size > 0
    ) {
      // Convert sets to arrays for comparison
      const prevDemos = Array.from(prevProps.selectedDemographics);
      const nextDemos = Array.from(nextProps.selectedDemographics);

      // Check if any demographics changed
      const hasChanges = prevDemos.some((prevDemo) => {
        return !nextDemos.some(
          (nextDemo) =>
            prevDemo.key === nextDemo.key && prevDemo.value === nextDemo.value
        );
      });

      if (hasChanges) {
        return false; // Re-render if demographic content changed
      }
    }

    // Check if showMovement changed
    if (prevProps.showMovement !== nextProps.showMovement) {
      return false;
    }

    // Check if comparisonPerspectives changed
    if (prevProps.comparisonPerspectives !== nextProps.comparisonPerspectives) {
      return false;
    }

    // Check if timelineProjects changed
    if (prevProps.timelineProjects !== nextProps.timelineProjects) {
      return false;
    }

    // Check if currentProjectId changed
    if (prevProps.currentProjectId !== nextProps.currentProjectId) {
      return false;
    }

    // Check if comparisonProjectId changed
    if (prevProps.comparisonProjectId !== nextProps.comparisonProjectId) {
      return false;
    }

    return true; // No need to re-render
  }
);
